import simplify from "simplify-geojson"
import { wktToGeoJSON } from "betterknown"
import { ACCESS_TOKEN } from "hooks/use_map"
import bbox from "@turf/bbox"
import type { Blockgroup, Prisma } from "db"
import type { GetScenario } from "app/scenarios/queries/getScenario"
import type { GeoJSONSource } from "mapbox-gl"
import type { QueryGeomEnvelope } from "tes-types"
import type { ScenarioArea } from "types/data"

// This module is the geo/presentation layer: every function below pulls in
// Mapbox/Blitz/turf. Pure scenario math lives in scenario_math.ts (no such runtime deps,
// unit-testable in isolation) — import math from there directly, not through this module.

/**
 * @param scenario with associated areas and blockgroups
 * @param blockgroup
 * @returns Areas array for the given blockgroup
 */
export function getAreasOnBlockgroup(scenario: GetScenario, blockgroup: Blockgroup) {
  return scenario.areas.filter((area: ScenarioArea) => area.area.blockgroupId === blockgroup.id)
}

export function getBbox(feature: QueryGeomEnvelope) {
  const geometry = wktToGeoJSON(feature[0].geom)
  if (!geometry) throw new Error("Failed to parse geometry from WKT")
  return bbox(geometry)
}

export function getSimplifiedGeojsonAndBbox(feature: QueryGeomEnvelope) {
  const geometry = feature && wktToGeoJSON(feature[0].geom)
  if (!geometry) throw new Error("Failed to parse geometry from WKT")
  const geojson = simplify(
    {
      type: "Feature",
      properties: { "fill-opacity": 0, stroke: "#F9B37A", "stroke-width": 5 },
      geometry,
    },
    0.0001
  )
  return { geojson: geojson as GeoJSONSource, bbox: bbox(geometry) }
}

export function getMinimapImageSrc(geojson: GeoJSONSource, extent: Prisma.JsonValue) {
  let output
  if (Array.isArray(geojson)) {
    output = geojson.map((g) => JSON.stringify(g)).join()
  } else {
    output = JSON.stringify(geojson)
  }
  return `https://api.mapbox.com/styles/v1/mapbox/light-v10/static/geojson(${encodeURIComponent(
    output
  )})/[${extent}]/800x800?padding=200,200&access_token=${ACCESS_TOKEN}`
}
